<template>
  <v-card flat>
    <v-tabs v-model="tab" grow>
      <v-tab>Requête</v-tab>
      <v-tab>Affichage</v-tab>
      <v-tab>Drag & drop</v-tab>
      <v-tab>Actions</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab" class="pa-4">
      <v-tab-item>
        <v-alert type="info" text dense>
          La table principale est <strong>{{ module.table }}</strong>. Donnez un alias stable à chaque valeur utilisée dans les cartes ou dans le payload du drag.
        </v-alert>
        <v-tabs v-model="queryTab" background-color="grey lighten-4">
          <v-tab>Champs</v-tab>
          <v-tab>Filtres et relations</v-tab>
        </v-tabs>
        <v-tabs-items v-model="queryTab">
          <v-tab-item class="pt-3">
            <Fields :modalKey="modalKey" :page="page" :champs="displays.params.champs" :filter="displays.filter" :table="module.table" />
          </v-tab-item>
          <v-tab-item class="pt-3">
            <Filtre :page="page" :filtre="displays.filter" :table="module.table" />
          </v-tab-item>
        </v-tabs-items>
        <v-text-field v-model.number="displays.params.display.maxItems" type="number" min="1" max="500" label="Nombre maximal d’éléments chargés" outlined dense class="mt-4" />
        <v-text-field v-model="displays.params.display.emptyText" label="Texte lorsque la liste est vide" outlined dense />
      </v-tab-item>

      <v-tab-item>
        <v-alert type="info" text dense>
          Utilisez <code>[[table||alias]]</code>. Les deux rendus réutilisent constructVisuel et les alias de la requête principale.
        </v-alert>
        <h3 class="mb-2">Carte déplaçable</h3>
        <Visuels :visuels="displays.params.cardVisual" :fields="visualFields" :page="page" :modalKey="modalKey" @recepVal="value => setParam('cardVisual', value)" />
        <v-divider class="my-5" />
        <v-switch v-model="displays.params.tooltip.enabled" label="Activer le tooltip" color="primary" />
        <v-row dense v-if="displays.params.tooltip.enabled">
          <v-col cols="12" md="6"><v-select v-model="displays.params.tooltip.position" :items="tooltipPositions" label="Position" outlined dense /></v-col>
          <v-col cols="12" md="6"><v-text-field v-model.number="displays.params.tooltip.minWidth" type="number" label="Largeur minimale" suffix="px" outlined dense /></v-col>
        </v-row>
        <Visuels v-if="displays.params.tooltip.enabled" :visuels="displays.params.tooltip.visual" :fields="visualFields" :page="page" :modalKey="modalKey" @recepVal="value => setTooltipVisual(value)" />
      </v-tab-item>

      <v-tab-item>
        <v-alert color="blue" border="left" text dense>
          Le canal et le type permettent aux modules récepteurs d’accepter ou refuser ce drag. Seuls les aliases explicitement mappés sont placés dans le payload.
        </v-alert>
        <v-switch v-model="displays.params.drag.enabled" label="Autoriser le déplacement" color="primary" />
        <v-switch
          v-model="displays.params.drag.calendarEnabled"
          label="Autoriser aussi le dépôt dans FullCalendar"
          color="primary"
          hint="Désactivez cette option pour une liste destinée uniquement à des zones HTML5, comme le module Répartition."
          persistent-hint
        />
        <v-row dense>
          <v-col cols="12" md="6"><v-text-field v-model="displays.params.drag.channel" label="Canal" placeholder="production-planning" outlined dense /></v-col>
          <v-col cols="12" md="6"><v-text-field v-model="displays.params.drag.entityType" label="Type d’entité" placeholder="production" outlined dense /></v-col>
        </v-row>
        <v-row dense>
          <v-col v-for="field in aliasSelectors" :key="field.key" cols="12" md="4">
            <v-autocomplete v-model="displays.params.drag[field.key]" :items="aliases" :label="field.label" clearable outlined dense />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="4">
            <v-select
              v-model="displays.params.drag.durationMode"
              :items="durationModes"
              label="Durée dans FullCalendar"
              outlined
              dense
            />
          </v-col>
          <v-col v-if="displays.params.drag.durationMode === 'alias'" cols="12" md="4">
            <v-autocomplete
              v-model="displays.params.drag.durationAlias"
              :items="aliases"
              label="Alias de durée"
              hint="Alias de la requête contenant par exemple 02:00 ou 2"
              persistent-hint
              clearable
              outlined
              dense
            />
          </v-col>
          <v-col v-if="displays.params.drag.durationMode === 'fixed'" cols="12" md="4">
            <v-text-field
              v-model.number="displays.params.drag.durationFixedHours"
              type="number"
              min="0.01"
              step="0.25"
              label="Durée fixe"
              suffix="heure(s)"
              hint="Exemple : 2 crée un créneau de 2 heures"
              persistent-hint
              outlined
              dense
            />
          </v-col>
        </v-row>

        <div class="d-flex align-center mt-4 mb-2">
          <h3>Valeurs envoyées dans le payload</h3>
          <v-spacer />
          <v-btn small color="primary" @click="addPayloadMapping"><v-icon left small>mdi-plus</v-icon>Ajouter</v-btn>
        </div>
        <v-row v-for="(mapping, index) in displays.params.drag.payloadMappings" :key="index" dense align="center">
          <v-col cols="12" md="5"><v-text-field v-model="mapping.key" label="Clé du payload" outlined dense hide-details /></v-col>
          <v-col cols="12" md="5"><v-autocomplete v-model="mapping.alias" :items="aliases" label="Alias source" outlined dense hide-details /></v-col>
          <v-col cols="12" md="2" class="text-right"><v-btn icon color="error" @click="removePayloadMapping(index)"><v-icon>mdi-delete</v-icon></v-btn></v-col>
        </v-row>

        <v-alert type="info" text dense class="mt-4">
          Enveloppe émise : <code>version</code>, <code>sourceModuleId</code>, <code>channel</code>, <code>entityType</code>, <code>entityId</code>, <code>quantity</code>, <code>concurrencyValue</code> et <code>payload</code>.
        </v-alert>
      </v-tab-item>
      <v-tab-item>
        <v-alert type="info" text dense>
          Toutes les actions créées pour ce module sont proposées sur chaque carte.
          Leurs conditions déterminent leur affichage, par exemple
          <code>[[line||restant]] &gt; 0</code>. Aucune affiliation supplémentaire n’est nécessaire.
        </v-alert>
        <v-autocomplete
          v-model="displays.params.reloadModules"
          :items="reloadModuleOptions"
          label="Modules à rafraîchir après une action"
          multiple chips deletable-chips clearable outlined dense
          hint="Après une action API réussie ou à la fermeture d’une fiche ouverte par une action. La liste se recharge elle-même."
          persistent-hint
        />
        <listActions :page="page" :module="module" :modalKey="modalKey" />
      </v-tab-item>
    </v-tabs-items>
  </v-card>
</template>

<script>
import { admin } from '../mixins'

export default {
  name: 'DraggableListAdmin',
  mixins: [admin],
  components: {
    Visuels: () => import('../../engine/outils/constructVisuel/Visuel.vue'),
  },
  data() {
    return {
      tab: 0,
      queryTab: 0,
      displays: this.module.displays,
      reloadModuleOptions: [],
      tooltipPositions: [
        { text: 'Droite', value: 'right' }, { text: 'Gauche', value: 'left' },
        { text: 'Haut', value: 'top' }, { text: 'Bas', value: 'bottom' },
      ],
      durationModes: [
        { text: 'Durée par défaut de FullCalendar', value: 'default' },
        { text: 'Durée provenant d’un alias de la requête', value: 'alias' },
        { text: 'Durée fixe définie manuellement', value: 'fixed' },
      ],
      aliasSelectors: [
        { key: 'idAlias', label: 'Alias de l’identifiant' },
        { key: 'titleAlias', label: 'Alias du titre' },
        { key: 'quantityAlias', label: 'Alias de quantité' },
        { key: 'concurrencyAlias', label: 'Alias de concurrence (updated_at)' },
      ],
    }
  },
  computed: {
    aliases() {
      return (this.displays.params.champs || [])
        .filter(field => field && field.as)
        .map(field => ({ text: `${field.as} (${field.field || field.type})`, value: field.as }))
    },
    visualFields() {
      return this.aliases.map(item => ({ ...item }))
    },
  },
  created() {
    if (!Array.isArray(this.displays.params.reloadModules)) this.$set(this.displays.params, 'reloadModules', [])
    if (!this.displays.params.drag.payloadMappings) this.$set(this.displays.params.drag, 'payloadMappings', [])
    if (this.displays.params.drag.calendarEnabled === undefined) this.$set(this.displays.params.drag, 'calendarEnabled', true)
    if (!this.displays.params.drag.durationMode) {
      this.$set(this.displays.params.drag, 'durationMode', this.displays.params.drag.durationAlias ? 'alias' : 'default')
    }
    if (this.displays.params.drag.durationFixedHours === undefined) this.$set(this.displays.params.drag, 'durationFixedHours', null)
  },
  mounted() {
    // L'API exige un filtre : les types viennent du registre des modules disponibles.
    const types = [...new Set(Object.values(this.$modules || {})
      .filter(plugin => plugin && plugin.name && plugin.name !== 'search' && plugin.components && plugin.components.admin)
      .map(plugin => plugin.name))]
    if (!types.length) return
    this.API({ type: 'post', url: '/engine/module/list', params: { type: types } }).then(response => {
      this.reloadModuleOptions = (Array.isArray(response.data) ? response.data : [])
        .filter(module => module.type !== 'search' && Number(module.id) !== Number(this.module.id))
        .map(module => ({ text: `${module.id} — ${module.name} (${module.type})`, value: module.id }))
    })
  },
  methods: {
    setParam(key, value) {
      this.$set(this.displays.params, key, value)
      this.$emit('recepVal', this.displays)
    },
    setTooltipVisual(value) {
      this.$set(this.displays.params.tooltip, 'visual', value)
      this.$emit('recepVal', this.displays)
    },
    addPayloadMapping() {
      this.displays.params.drag.payloadMappings.push({ key: '', alias: '' })
    },
    removePayloadMapping(index) {
      this.displays.params.drag.payloadMappings.splice(index, 1)
    },
  },
}
</script>
